import { escapeHtml, linkifyPlainText, normalizeLinkUrl } from './autoLink';

export { escapeHtml, normalizeLinkUrl };

export type TextAlign = 'left' | 'center' | 'right';

type InlineTag = 'b' | 'i' | 'u' | 's' | 'a';

type OpenTag = { tag: InlineTag; open: string };

type Line = { html: string; align: TextAlign | null; hasContent: boolean };

const INLINE_TAGS: Record<string, InlineTag> = {
  B: 'b',
  STRONG: 'b',
  I: 'i',
  EM: 'i',
  U: 'u',
  S: 's',
  STRIKE: 's',
  DEL: 's',
  A: 'a',
};

const BLOCK_TAGS = new Set([
  'DIV',
  'P',
  'LI',
  'BLOCKQUOTE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
]);

const readAlign = (el: HTMLElement): TextAlign | null => {
  const value = (el.style?.textAlign || el.getAttribute('align') || '')
    .toLowerCase()
    .trim();
  if (value === 'center' || value === 'right') return value;
  if (value === 'left' || value === 'start') return 'left';
  return null;
};

export const serializeEditorContent = (root: HTMLElement): string => {
  if (!(root.textContent || '').trim()) return '';

  const lines: Line[] = [];
  const stack: OpenTag[] = [];
  let current: Line = { html: '', align: null, hasContent: false };

  const pushLine = (align: TextAlign | null) => {
    const closing = [...stack]
      .reverse()
      .map(({ tag }) => `</${tag}>`)
      .join('');
    lines.push({
      html: current.html + closing,
      align: current.hasContent ? current.align : align,
      hasContent: current.hasContent,
    });
    current = {
      html: stack.map(({ open }) => open).join(''),
      align: null,
      hasContent: false,
    };
  };

  const appendText = (
    text: string,
    align: TextAlign | null,
    inLink: boolean,
  ) => {
    if (!text) return;
    if (!current.hasContent) {
      current.align = align;
      current.hasContent = true;
    }
    current.html += inLink ? escapeHtml(text) : linkifyPlainText(text);
  };

  const walk = (node: Node, align: TextAlign | null, inLink = false) => {
    if (node.nodeType === Node.TEXT_NODE) {
      appendText(
        (node.textContent || '').replace(/[\r\n]+/g, ' '),
        align,
        inLink,
      );
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;
    const name = el.tagName;

    if (name === 'BR') {
      pushLine(align);
      return;
    }

    if (BLOCK_TAGS.has(name)) {
      const blockAlign = readAlign(el) ?? align;
      if (current.hasContent) pushLine(align);
      const linesBefore = lines.length;
      el.childNodes.forEach((child) => walk(child, blockAlign, inLink));
      if (current.hasContent) pushLine(blockAlign);
      else if (lines.length === linesBefore && el.childNodes.length > 0) {
        pushLine(blockAlign);
      }
      return;
    }

    const tag = INLINE_TAGS[name];
    let open: string | null = null;
    if (tag === 'a') {
      const href = normalizeLinkUrl(el.getAttribute('href') || '');
      if (href) open = `<a href="${escapeHtml(href)}">`;
    } else if (tag && !stack.some((entry) => entry.tag === tag)) {
      open = `<${tag}>`;
    }

    if (open) {
      stack.push({ tag, open });
      current.html += open;
    }
    el.childNodes.forEach((child) =>
      walk(child, align, inLink || (tag === 'a' && !!open)),
    );
    if (open) {
      stack.pop();
      current.html += `</${tag}>`;
    }
  };

  root.childNodes.forEach((child) => walk(child, null));
  if (current.hasContent) pushLine(null);

  while (lines.length && !lines[lines.length - 1].hasContent) lines.pop();

  const cleanLines = lines.map((line) => ({
    ...line,
    html: line.html
      .replace(/<(b|i|u|s)><\/\1>/g, '')
      .replace(/<a href="[^"]*"><\/a>/g, ''),
  }));

  if (!cleanLines.some((line) => line.align && line.align !== 'left')) {
    return cleanLines.map((line) => line.html).join('\n');
  }

  return cleanLines
    .map(
      (line) =>
        `<span style="display:block;text-align:${line.align || 'left'}">${
          line.html || '<br />'
        }</span>`,
    )
    .join('');
};

export const bodyToEditorHtml = (body: string): string =>
  (body || '').replace(/\n/g, '<br>');

export const bodyToPreviewHtml = (body: string): string =>
  (body || '')
    .replace(/\n/g, '<br />')
    .replace(
      /<a href="/g,
      '<a target="_blank" rel="noopener noreferrer" href="',
    );
